// Reads /api/runtime on mount once and caches the result. The endpoint
// is dirt-cheap (env var read, no DB hit, no auth) so every page can
// fan it in without worrying about latency.
//
// Drives the Training Mode banner in App.tsx and any other surfaces
// that want to know:
//   * Are we in a known training day? (training_mode is a date string)
//   * How many attendees are expected? (attendee_count)
//   * Is browser automation disabled? (browser_automation_disabled)
//
// The values are STATIC for the life of a dyno — once set in Heroku
// config, they don't change until release. We can therefore cache
// once and never refetch in this hook; tanstack-query handles a fresh
// fetch on hard reload.

import { useQuery } from "@tanstack/react-query";
import { apiSoft } from "@/lib/api/http";

export interface CircuitBreakerState {
  engaged: boolean;
  reason?: string;
  expires_at?: number;
}

export interface RuntimeInfo {
  training_mode: string | null;
  attendee_count: number | null;
  workers_per_dyno: number;
  browser_automation_disabled: boolean;
  release_version: string;
  circuit_breaker?: CircuitBreakerState;
}

const RUNTIME_QUERY_KEY = ["runtime"] as const;

export function useRuntime(): {
  loading: boolean;
  runtime: RuntimeInfo | null;
  isTrainingDay: boolean;
  circuitBreaker: CircuitBreakerState;
} {
  const { data, isLoading } = useQuery({
    queryKey: RUNTIME_QUERY_KEY,
    queryFn: async (): Promise<RuntimeInfo | null> => {
      const result = await apiSoft<RuntimeInfo>("/api/runtime");
      return result ?? null;
    },
    // Long stale-time — env vars don't change until next deploy. The
    // circuit_breaker field IS dynamic, but the V2 Configure page polls
    // /api/runtime explicitly when it lands, and the breaker has its
    // own short-poll wrapper below. Don't aggressively refetch here
    // because the static fields are 99% of the response.
    staleTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    // Round-3: poll every 30s during training so the circuit-breaker
    // state propagates across the fleet within a single trainee's
    // attention span (engage at t=0, all trainees see it by t=30s).
    refetchInterval: 30_000,
  });
  return {
    loading: isLoading,
    runtime: data ?? null,
    isTrainingDay: !!data?.training_mode,
    circuitBreaker: data?.circuit_breaker ?? { engaged: false },
  };
}
